import {BleDevice} from "@hybird/device/protocol/ble";
import {defaultError, WebError} from "@hybird/core/error";
import {deviceError} from "@hybird/device/deviceError";
import {DeviceEvent} from "@hybird/core/device/protocol";
import {flutterBluetooth} from "@hybird/flutter/bridge/blutooth";
import {flutterCore} from "@hybird/flutter/bridge/core";
import {isSupport} from "@hybird/flutter/bridge";

class FlutterBluetoothDevice extends BleDevice {
  /**
   *
   * @type {{
   *   "deviceId": String,
   *   "characteristicId": String,
   *   "serviceId": String,
   *   "properties": {
   *     "read": boolean,
   *     "write":boolean,
   *     "notify": boolean,
   *     "indicate": boolean,
   *   }
   * }|null}
   */
  readCharacteristic = null;

  /**
   *
   * @type {[{
   *   "deviceId": String,
   *   "characteristicId": String,
   *   "serviceId": String,
   *   "properties": {
   *     "read": boolean,
   *     "write":boolean,
   *     "notify": boolean,
   *     "indicate": boolean,
   *   }
   * }]}
   */
  writeCharacteristic = [];

  /**
   *
   * @type {{deviceId:String, name:String}|null}
   */
  scanResult = null;

  async _connect({clear, resolve, reject}) {
    this.readCharacteristic = null;
    this.writeCharacteristic = [];

    if (this.scanResult && this.connected) {
      return;
    }

    const requestPermission = await flutterBluetooth.requestPermissionBluetooth();
    const bluetoothAdapterState = await flutterBluetooth.getBluetoothAdapterState();

    if (bluetoothAdapterState.includes("off")) {
      if (this.config.blue.tureOn instanceof Function) {
        await this.config.blue.tureOn();
      }

      const bluetoothTureOn = await flutterBluetooth.bluetoothTureOn().catch(() => {
        reject(WebError.code(deviceError.NOT_OPEN_BLUETOOTH));
      });

      if (!bluetoothTureOn) {
        reject(WebError.code(deviceError.NOT_OPEN_BLUETOOTH));
      }
    }

    if (!requestPermission) {
      reject(WebError.code(defaultError.NOT_PERMISSION, {msg: "蓝牙权限未授权, 请前往设置授权"}));
    }

    clear(async () => {
      await flutterBluetooth.stopBluetoothDevicesDiscovery();
    });

    await flutterBluetooth.startBluetoothDevicesDiscovery({
      services: this.serviceUUIDs, deviceId: this.config.blue.mac, names: [this.name], on: {
        onBluetoothDeviceFound: async (result) => {
          if (this.connectStatus !== 1 && this.connectStatus !== 3) {
            console.warn("未知情况出现非连接中进入此方法", this.connectStatus);
            return;
          }

          console.log('扫描', JSON.stringify(result))
          if (result.name === this.name) {
            const deviceId = result.deviceId;
            this.scanResult = result;
            console.log('扫描结果', JSON.stringify(result))
            await flutterBluetooth.stopBluetoothDevicesDiscovery();
            this.connectByDeviceId(clear, deviceId).then(resolve).catch(reject);
          }
        }
      }
    })
  }

  async connectByDeviceId(clear, deviceId) {
    try {
      await flutterBluetooth.connectBLEDevice(deviceId, 5);

      clear(async () => {
        await this.disconnect();
      });

      const message = data => {
        if (data.deviceId === deviceId) {
          if (data.value.includes("RESTART")) {
            console.log(data);
          }
          this._onMessage(data.value);
        }
      };

      const onDisconnect = (data) => {
        this.scanResult = null;
        flutterBluetooth.offBluetoothCharacteristicValueChange(message);
        cancel();
        onBluetoothAdapterStateChangeCancel();
        this.emit(DeviceEvent.DISCONNECTED);

        console.log("flutter蓝牙断开连接", JSON.stringify(data))
      };

      const {cancel} = await flutterBluetooth.onBluetoothConnectionStateChange(deviceId, (data) => {
        if (!data.state) {
          onDisconnect(data);
        }
      })

      const onBluetoothAdapterStateChange = await flutterBluetooth.onBluetoothAdapterStateChange((data) => {
        if (data.state !== "BluetoothAdapterState.on" && data.state !== "on") {
          onDisconnect(data);
        }
      });

      const onBluetoothAdapterStateChangeCancel = onBluetoothAdapterStateChange.cancel;

      const services = await flutterBluetooth.getBLEDeviceServices(deviceId);

      let characteristicUUIDs = {};
      if (this.characteristicUUIDs instanceof Array) {
        for (let i in this.characteristicUUIDs) {
          characteristicUUIDs[this.characteristicUUIDs[i]] = "read";
        }
      } else {
        Object.assign(characteristicUUIDs, this.characteristicUUIDs);
      }

      this.onBluetoothCharacteristicValueChange = await flutterBluetooth.onBluetoothCharacteristicValueChange(message);

      for (const i in this.serviceUUIDs) {
        const service = services.find(e => e.serviceId === this.serviceUUIDs[i]);

        if (!service) {
          continue;
        }

        const serviceId = service.serviceId;
        const characteristics = await flutterBluetooth.getBLEDeviceCharacteristics(deviceId, serviceId);

        for (const i in characteristics) {
          const characteristic = characteristics[i];
          const characteristicId = characteristics[i].characteristicId;
          console.log(characteristicId);

          const type = characteristicUUIDs[characteristicId];
          if (type === "write") {
            this.writeCharacteristic.push(characteristic);
          }

          if (type === "read") {
            await flutterBluetooth.notifyBLECharacteristicValueChange(deviceId, serviceId, characteristicId, true);

            this.readCharacteristic = characteristic;
          }
        }
      }

      if (!this.readCharacteristic) {
        throw WebError.code(deviceError.CONNECT_ILLEGAL_ARGUMENT, {msg: "该设备无法使用蓝牙连接"});
      }

      if (this.writeCharacteristic.length === 0) {
        this.writeCharacteristic.push(this.readCharacteristic);
      }
    } catch (e) {
      this.scanResult = null;
      this.disconnect();
      console.error(e);

      if (deviceError.isError(e, deviceError.FLUTTER_INIT_ERROR) && e.error?.data?.code === 7014) {
        throw WebError.error(e, deviceError.CONNECT_ILLEGAL_ARGUMENT, e.error?.data?.errMsg);
      } else {
        throw WebError.error(e, deviceError.CONNECT_ILLEGAL_ARGUMENT);
      }
    }
  }

  async _write({clear, buffer, resolve, reject}) {
    let send = false;
    for (let i = 0; i < 3; i++) {
      if (!this.scanResult) {
        reject(WebError.error(deviceError.NOT_BLE_CONNECT));
      }
      for (const i in this.writeCharacteristic) {
        const characteristic = this.writeCharacteristic[i];
        try {
          await flutterBluetooth.writeBLECharacteristicValue(characteristic.deviceId, characteristic.serviceId, characteristic.characteristicId, buffer.toString("hex"));
          send = true;
          break;
        } catch (e) {
          console.error("ble write error", e);
        }
      }

      if (send) {
        resolve();
        return;
      }

      resolve();
    }

    reject(WebError.error(deviceError.WRITE_TIMEOUT));
  }

  get state() {
    return this.connected ? "connected" : "disconnected";
  }

  get connected() {
    if (this.scanResult) {
      return flutterBluetooth.getBLEConnectionState(this.scanResult.deviceId)
    } else {
      return false;
    }
  }

  async _disconnect() {
    if (this.connected && await flutterBluetooth.getBluetoothAdapterState()) {
      await flutterBluetooth.disconnectBLEDevice(this.scanResult.deviceId);
    }

    this.onBluetoothCharacteristicValueChange?.cancel();
  }
}

const createFlutterBluetoothDevice = {
  create: (config) => {
    return new FlutterBluetoothDevice(config.blue.name, {
      serviceUUIDs: config.blue.serviceUUIDs, characteristicUUIDs: config.blue.characteristicUUIDs
    }, config);
  }, isSupport() {
    return window && isSupport()
  },
  /**
   *
   * @param config
   * @param device{BleDevice}
   * @return {boolean}
   */
  canConnect: (config, device) => {
    //兼容当正在连接时出现还有在连接的情况处理
    if (device && device.connectStatus === 1) {
      device.connectStatus = 3
      return false;
    }

    return config.blue.name === device.name;
  }
};

export {
  FlutterBluetoothDevice, createFlutterBluetoothDevice
};